import type { Middleware } from '@reduxjs/toolkit'
import { track } from '@adthlete/shared-frontend'

type Props = Record<string, string | number | boolean | null>
type Arg = any

/**
 * The brand actions recorded for the admin Behavior page, by the API call that does them: when
 * one succeeds, its step is tracked. One list instead of a track() on every screen that can
 * send an offer or approve content. Props carry ids and choices only, never message text.
 */
const ACTIONS: Record<string, (arg: Arg) => [string, Props?] | null> = {
  createCampaign: (a) => (a?.saveAsDraft ? ['campaign_draft_saved'] : ['campaign_published']),
  archiveCampaign: (id) => ['campaign_archived', { campaignId: String(id) }],
  deleteCampaign: (id) => ['campaign_deleted', { campaignId: String(id) }],
  setShortlisted: (a) => [a?.on ? 'athlete_shortlisted' : 'athlete_unshortlisted'],
  createCollaboration: (a) => [
    'offer_sent',
    { campaignId: a?.campaignId ?? a?.campaign_id ?? null },
  ],
  updateOfferTerms: (a) => ['offer_terms_changed', { dealId: a?.id ?? null }],
  respondToApplication: (a) => [
    a?.action === 'accept'
      ? 'application_accepted'
      : a?.action === 'counter'
        ? 'application_countered'
        : 'application_passed',
    { applicationId: a?.id ?? null },
  ],
  reviewSubmission: (a) => [
    a?.action === 'approve' ? 'content_approved' : 'content_changes_requested',
    { dealId: a?.collaborationId ?? null },
  ],
  completeBrandStep: (a) => ['deal_step_completed', { dealId: a?.collaborationId ?? null }],
  extendLicenseAtRateCard: (a) => [
    'license_extended',
    { dealId: a?.collaborationId ?? null, days: a?.days ?? null },
  ],
  createDirectMessageThread: (a) => ['athlete_messaged', { fromTalentSheet: !!a?.talentSheetId }],
  sendCollaborationMessage: (a) => ['deal_message_sent', { dealId: a?.collaborationId ?? null }],
  confirmPaymentMethodSetup: () => ['payment_method_added'],
  updateOrganization: () => ['brand_profile_saved'],
  saveProduct: () => ['product_saved'],
  acceptInvitation: () => ['invitation_accepted'],
}

export const actionTracking: Middleware = () => (next) => (action: any) => {
  const result = next(action)
  const meta = action?.meta
  if (meta?.requestStatus === 'fulfilled' && meta?.arg?.type === 'mutation') {
    try {
      const step = ACTIONS[meta.arg.endpointName]?.(meta.arg.originalArgs)
      if (step) track(step[0], step[1])
    } catch {
      // Tracking never gets in the way
    }
  }
  return result
}
